import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import {
  FiClock, FiDollarSign, FiAlertTriangle, FiUnlock,
  FiFilter, FiDownload, FiPrinter
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import { printZReport } from '../pos/POSZReport';

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-PK', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

const POSShiftReport = () => {
  const { activeCompany, activeBranch } = useAuth();

  const [shifts, setShifts] = useState([]);
  const [counters, setCounters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [counterFilter, setCounterFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(firstDay.toISOString().split('T')[0]);
    setDateTo(today.toISOString().split('T')[0]);
  }, []);

  // Counters for the filter dropdown
  useEffect(() => {
    if (!activeCompany?.id) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'companies', activeCompany.id, 'posCounters'));
        setCounters(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error('Error loading counters:', e); }
    })();
  }, [activeCompany]);

  const loadShifts = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    setHasLoaded(true);
    try {
      const ref = collection(db, 'companies', activeCompany.id, 'posShifts');
      let snap;
      try {
        snap = await getDocs(query(ref, orderBy('openedAt', 'desc')));
      } catch {
        snap = await getDocs(ref); // no index / empty — fall back
      }
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Branch scope (skip for 'all')
      if (activeBranch && activeBranch.id !== 'all') {
        list = list.filter(s => s.branchId === activeBranch.id);
      }
      if (counterFilter !== 'all') list = list.filter(s => s.counterId === counterFilter);
      if (statusFilter !== 'all') list = list.filter(s => (s.status || 'open') === statusFilter);

      // Date range on the opened date
      const openDate = (s) => (s.openedAt || '').split('T')[0];
      if (dateFrom) list = list.filter(s => openDate(s) >= dateFrom);
      if (dateTo) list = list.filter(s => openDate(s) <= dateTo);

      list.sort((a, b) => String(b.openedAt || '').localeCompare(String(a.openedAt || '')));
      setShifts(list);
    } catch (error) {
      console.error('Error loading shifts:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeBranch, counterFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (activeCompany?.id && dateFrom && dateTo) loadShifts();
  }, [activeCompany, dateFrom, dateTo, counterFilter, statusFilter, loadShifts]);

  const summary = useMemo(() => {
    const closed = shifts.filter(s => s.status === 'closed');
    const open = shifts.filter(s => s.status !== 'closed');
    const cashSales = closed.reduce((s, x) => s + (Number(x.systemCashSales) || 0), 0);
    const variance = closed.reduce((s, x) => s + (Number(x.variance) || 0), 0);
    return {
      total: shifts.length,
      openCount: open.length,
      closedCount: closed.length,
      cashSales,
      variance
    };
  }, [shifts]);

  const handleExport = () => {
    if (shifts.length === 0) return;
    let csv = `POS Shift / Z Report\n`;
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'}\n\n`;
    csv += `Shift#,Counter,Branch,Opened By,Opened At,Closed By,Closed At,Opening,Cash Sales,Cash Refunds,Card,Credit,Expected,Counted,Variance,Status\n`;
    shifts.forEach(s => {
      csv += [
        s.shiftNumber || '', `"${s.counterName || ''}"`, `"${s.branchName || ''}"`,
        `"${s.openedByName || ''}"`, fmtDateTime(s.openedAt), `"${s.closedByName || ''}"`, fmtDateTime(s.closedAt),
        Number(s.openingCash) || 0, Number(s.systemCashSales) || 0, Number(s.systemCashReturns) || 0,
        Number(s.systemCardSales) || 0, Number(s.systemCreditSales) || 0,
        Number(s.expectedCash) || 0, Number(s.countedCash) || 0, Number(s.variance) || 0, s.status || 'open'
      ].join(',') + '\n';
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `POS_Shift_Report_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <PageLayout pageTitle="POS Shift / Z Report" breadcrumb="Reports / Sale Reports / POS Shift Report">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Counter</Label>
            <Select value={counterFilter} onChange={e => setCounterFilter(e.target.value)}>
              <option value="all">All Counters</option>
              {counters.map(c => (
                <option key={c.id} value={c.id}>{c.counterName} ({c.counterCode})</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>
        <ButtonGroup>
          <Button onClick={handleExport} disabled={shifts.length === 0}><FiDownload /> Export CSV</Button>
          <Button primary onClick={loadShifts} disabled={loading}><FiFilter /> {loading ? 'Loading...' : 'Refresh'}</Button>
        </ButtonGroup>
      </FiltersCard>

      {hasLoaded && (
        <SummaryCards>
          <SummaryCardStyled>
            <SummaryIcon color={colors.primary.main}><FiClock /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Shifts</SummaryLabel>
              <SummaryValue>{formatNumber(summary.total)}</SummaryValue>
              <SummarySubtext>{summary.closedCount} closed</SummarySubtext>
            </SummaryInfo>
          </SummaryCardStyled>
          <SummaryCardStyled>
            <SummaryIcon color={colors.success.main}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Cash Sales (closed)</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.cashSales)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>
          <SummaryCardStyled>
            <SummaryIcon color={Math.abs(summary.variance) < 0.01 ? colors.success.main : colors.error.main}><FiAlertTriangle /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Variance</SummaryLabel>
              <SummaryValue style={{ color: Math.abs(summary.variance) < 0.01 ? colors.text.primary : colors.error.main }}>
                {formatCurrency(summary.variance)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>
          <SummaryCardStyled>
            <SummaryIcon color={colors.warning.main}><FiUnlock /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Open Shifts</SummaryLabel>
              <SummaryValue>{formatNumber(summary.openCount)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>
        </SummaryCards>
      )}

      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState><EmptyText>Loading shifts...</EmptyText></EmptyState>
          ) : shifts.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiClock /></EmptyIcon>
              <EmptyText>No shifts found for the selected filters</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Shift #</Th>
                  <Th>Counter</Th>
                  <Th>Opened</Th>
                  <Th>Closed</Th>
                  <Th align="right">Opening</Th>
                  <Th align="right">Cash Sales</Th>
                  <Th align="right">Expected</Th>
                  <Th align="right">Counted</Th>
                  <Th align="right">Variance</Th>
                  <Th align="center">Status</Th>
                  <Th align="center">Z</Th>
                </Tr>
              </Thead>
              <Tbody>
                {shifts.map(s => {
                  const isClosed = s.status === 'closed';
                  const variance = Number(s.variance) || 0;
                  return (
                    <Tr key={s.id}>
                      <Td style={{ fontWeight: 600 }}>{s.shiftNumber || '-'}</Td>
                      <Td>
                        <div>{s.counterName || '-'}</div>
                        {s.branchName && <Sub>{s.branchName}</Sub>}
                      </Td>
                      <Td>
                        <div>{fmtDateTime(s.openedAt)}</div>
                        <Sub>{s.openedByName || ''}</Sub>
                      </Td>
                      <Td>
                        <div>{isClosed ? fmtDateTime(s.closedAt) : '—'}</div>
                        {isClosed && <Sub>{s.closedByName || ''}</Sub>}
                      </Td>
                      <AmountCell align="right">{formatCurrency(s.openingCash || 0)}</AmountCell>
                      <AmountCell align="right">{isClosed ? formatCurrency(s.systemCashSales || 0) : '—'}</AmountCell>
                      <AmountCell align="right">{isClosed ? formatCurrency(s.expectedCash || 0) : '—'}</AmountCell>
                      <AmountCell align="right">{isClosed ? formatCurrency(s.countedCash || 0) : '—'}</AmountCell>
                      <AmountCell align="right" style={{ color: isClosed && Math.abs(variance) >= 0.01 ? colors.error.main : colors.text.primary, fontWeight: 600 }}>
                        {isClosed ? formatCurrency(variance) : '—'}
                      </AmountCell>
                      <Td align="center">
                        <StatusBadge $closed={isClosed}>{isClosed ? 'Closed' : 'Open'}</StatusBadge>
                      </Td>
                      <Td align="center">
                        {isClosed && (
                          <IconBtn onClick={() => printZReport(s, activeCompany)} title="Reprint Z-report">
                            <FiPrinter size={14} />
                          </IconBtn>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

// ── Styled ──
const FiltersCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm}; padding: 20px; margin-bottom: 20px;
`;
const FiltersGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px; margin-bottom: 16px;
`;
const FilterGroup = styled.div` display: flex; flex-direction: column; gap: 6px; `;
const Label = styled.label`
  font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase;
`;
const Input = styled.input`
  padding: 10px 12px; border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md}; font-size: 14px; color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const Select = styled.select`
  padding: 9px 12px; border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md}; font-size: 13px; color: ${colors.text.primary};
  background: white; cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const ButtonGroup = styled.div` display: flex; gap: 12px; justify-content: flex-end; `;
const Button = styled.button`
  padding: 10px 20px; border: none; border-radius: ${colors.borderRadius.md};
  font-size: 14px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 8px;
  ${props => props.primary ? `background: ${colors.primary.main}; color: white; &:hover { background: ${colors.primary.dark}; }`
    : `background: ${colors.neutral[100]}; color: ${colors.text.primary}; &:hover { background: ${colors.neutral[200]}; }`}
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const SummaryCards = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-bottom: 20px;
`;
const SummaryCardStyled = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm};
  padding: 20px; display: flex; align-items: center; gap: 16px;
`;
const SummaryIcon = styled.div`
  width: 48px; height: 48px; border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15; color: ${props => props.color || colors.primary.main};
  display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0;
`;
const SummaryInfo = styled.div` min-width: 0; `;
const SummaryLabel = styled.div` font-size: 12px; color: ${colors.text.secondary}; margin-bottom: 4px; `;
const SummaryValue = styled.div` font-size: 22px; font-weight: 600; color: ${colors.text.primary}; `;
const SummarySubtext = styled.div` font-size: 12px; color: ${colors.text.tertiary}; margin-top: 2px; `;
const ContentCard = styled.div`
  background: white; border-radius: ${colors.borderRadius.lg}; box-shadow: ${colors.shadows.sm}; overflow: hidden;
`;
const TableContainer = styled.div` overflow-x: auto; `;
const Table = styled.table` width: 100%; border-collapse: collapse; `;
const Thead = styled.thead` background: ${colors.neutral[50]}; `;
const Th = styled.th`
  padding: 12px 16px; text-align: ${props => props.align || 'left'};
  font-size: 12px; font-weight: 600; color: ${colors.text.secondary};
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]}; white-space: nowrap;
`;
const Tbody = styled.tbody``;
const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
`;
const Td = styled.td`
  padding: 12px 16px; font-size: 14px; color: ${colors.text.primary}; text-align: ${props => props.align || 'left'};
`;
const Sub = styled.div` font-size: 11px; color: ${colors.text.tertiary}; margin-top: 2px; `;
const AmountCell = styled(Td)` font-family: 'SF Mono', 'Consolas', monospace; font-size: 13px; `;
const StatusBadge = styled.span`
  display: inline-block; padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600;
  background: ${props => props.$closed ? colors.neutral[200] : '#E8F5E9'};
  color: ${props => props.$closed ? colors.text.secondary : '#2E7D32'};
`;
const IconBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.sm}; background: white; color: ${colors.text.secondary}; cursor: pointer;
  &:hover { border-color: ${colors.primary.main}; color: ${colors.primary.main}; }
`;
const EmptyState = styled.div` text-align: center; padding: 60px 20px; color: ${colors.text.secondary}; `;
const EmptyIcon = styled.div` font-size: 48px; color: ${colors.neutral[300]}; margin-bottom: 16px; `;
const EmptyText = styled.p` font-size: 16px; margin-bottom: 8px; `;

export default POSShiftReport;
